import { useState } from 'react'
import { Eye } from 'lucide-react'
import Card from '../components/Card'

export default function ColorBlindness() {
  const [color, setColor] = useState('#3b82f6')
  const [type, setType] = useState('protanopia')

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }).join('')
  }

  const simulateColorBlindness = (rgb, type) => {
    // Color blindness simulation matrices
    const matrices = {
      protanopia: [
        [0.567, 0.433, 0],
        [0.558, 0.442, 0],
        [0, 0.242, 0.758]
      ],
      deuteranopia: [
        [0.625, 0.375, 0],
        [0.7, 0.3, 0],
        [0, 0.3, 0.7]
      ],
      tritanopia: [
        [0.95, 0.05, 0],
        [0, 0.433, 0.567],
        [0, 0.475, 0.525]
      ]
    }

    const matrix = matrices[type] || matrices.protanopia
    const r = rgb.r / 255
    const g = rgb.g / 255
    const b = rgb.b / 255

    const newR = Math.round((matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b) * 255)
    const newG = Math.round((matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b) * 255)
    const newB = Math.round((matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b) * 255)

    return rgbToHex(
      Math.max(0, Math.min(255, newR)),
      Math.max(0, Math.min(255, newG)),
      Math.max(0, Math.min(255, newB))
    )
  }

  const rgb = hexToRgb(color)
  const simulated = rgb ? simulateColorBlindness(rgb, type) : color

  const types = {
    protanopia: 'Protanopia (Red-blind)',
    deuteranopia: 'Deuteranopia (Green-blind)',
    tritanopia: 'Tritanopia (Blue-blind)'
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-3">Color:</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="input flex-1"
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Color Blindness Type:</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="input w-full"
              >
                <option value="protanopia">Protanopia (Red-blind)</option>
                <option value="deuteranopia">Deuteranopia (Green-blind)</option>
                <option value="tritanopia">Tritanopia (Blue-blind)</option>
              </select>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Original Color</h3>
            <div className="space-y-4">
              <div
                className="w-full h-32 rounded-lg border-2 border-gray-200"
                style={{ backgroundColor: color }}
              />
              <div className="text-center">
                <p className="text-sm font-mono text-gray-700">{color}</p>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Simulated View</h3>
              <Eye className="w-5 h-5 text-gray-600" />
            </div>
            <div className="space-y-4">
              <div
                className="w-full h-32 rounded-lg border-2 border-gray-200"
                style={{ backgroundColor: simulated }}
              />
              <div className="text-center">
                <p className="text-sm font-mono text-gray-700">{simulated}</p>
                <p className="text-xs text-gray-500 mt-1">{types[type]}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

